import { memo, useCallback, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";

import { ButtonV1 } from "components";
import { useDownload } from "hooks";
import { ACCEPTED_UPLOAD_FILE_TYPES } from "../constants";

const ICONS_BY_KEY = {
  file: InsertDriveFileOutlinedIcon,
  check: FactCheckOutlinedIcon,
};

const UploadSectionRow = ({ section, isUploading, onUpload }) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const { downloadDocument } = useDownload();
  const Icon = ICONS_BY_KEY[section.icon];
  const isDisabled = Boolean(section.isDisabled);

  const handleChooseFile = useCallback(() => {
    if (isDisabled) return;
    inputRef.current?.click();
  }, [isDisabled]);

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      event.target.value = null;

      if (file && !isDisabled) {
        onUpload(section, file);
      }
    },
    [isDisabled, onUpload, section],
  );

  const handleDownloadTemplate = useCallback(() => {
    if (isDisabled) return;
    downloadDocument(section.templateUrl, section.templateFileName, {
      actionType: section.actionType,
    });
  }, [downloadDocument, isDisabled, section]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2.5,
        border: `1px solid ${theme.palette.grey[900]}`,
        borderRadius: 2,
        p: 3,
        mb: 2,
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
        "&:hover": {
          borderColor: isDisabled ? theme.palette.grey[900] : theme.palette.primary.main,
          bgcolor: isDisabled ? "transparent" : theme.palette.info.lightest,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: theme.palette.info.lightest,
          color: theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>

      <Box flex={1}>
        <Typography fontWeight={600}>{section.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {section.description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        <ButtonV1
          title="Choose file"
          loader={isUploading}
          disabled={isDisabled}
          onClick={handleChooseFile}
        />
        <input
          type="file"
          ref={inputRef}
          style={{ display: "none" }}
          accept={ACCEPTED_UPLOAD_FILE_TYPES}
          onChange={handleFileChange}
          disabled={isDisabled}
        />
        <Typography
          variant="body2"
          onClick={handleDownloadTemplate}
          sx={{
            color: theme.palette.primary.main,
            cursor: isDisabled ? "default" : "pointer",
            textDecoration: "underline",
            marginTop: 1,
          }}
        >
          Download template
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(UploadSectionRow);
